import {
  CANDIDATE_STATUS,
  EVALUATION_ROUNDS,
  getCandidateStatusLabel,
  getEvaluationRoundLabel,
} from "@/types/enums";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info, GitBranch, Filter, BookOpen } from "lucide-react";

const lifecycleSteps = [
  {
    step: "1. Candidate registers",
    round: EVALUATION_ROUNDS.CV_PROFILE,
    status: CANDIDATE_STATUS.CV_SUBMITTED,
    evaluationStatus: "Not evaluated yet",
    notes:
      "This is the initial state after the CV is submitted. The candidate is in the CV/Profile round.",
  },
  {
    step: "2. AI evaluates the CV",
    round: EVALUATION_ROUNDS.CV_PROFILE,
    status: CANDIDATE_STATUS.PASSED,
    evaluationStatus: CANDIDATE_STATUS.PASSED,
    notes:
      "A passing CV keeps the candidate in CV/Profile, but now the CV round result is Passed and the candidate is ready for the assessment invite.",
  },
  {
    step: "3. AI rejects the CV",
    round: EVALUATION_ROUNDS.CV_PROFILE,
    status: CANDIDATE_STATUS.FAILED,
    evaluationStatus: CANDIDATE_STATUS.FAILED,
    notes:
      "A failed CV ends the flow unless someone manually changes the candidate later.",
  },
  {
    step: "4. Assessment email is sent",
    round: EVALUATION_ROUNDS.MCQ,
    status: CANDIDATE_STATUS.ASSESSMENT_SENT,
    evaluationStatus: "CV round remains Passed",
    notes:
      "When the invite is sent, the candidate moves from CV/Profile to the assessment stage. Email Sent becomes Yes.",
  },
  {
    step: "5. Candidate starts assessment",
    round: EVALUATION_ROUNDS.MCQ,
    status: CANDIDATE_STATUS.ASSESSMENT_IN_PROGRESS,
    evaluationStatus: "Assessment not graded yet",
    notes:
      "The candidate has opened the assessment and started answering.",
  },
  {
    step: "6. Candidate submits assessment",
    round: EVALUATION_ROUNDS.MCQ,
    status: CANDIDATE_STATUS.ASSESSMENT_COMPLETED,
    evaluationStatus: "Submitted, awaiting AI evaluation",
    notes:
      "This is the technical system state immediately after submission. Admins may think of it as Submitted.",
  },
  {
    step: "7. AI evaluates assessment",
    round: EVALUATION_ROUNDS.OPEN_ENDED,
    status: CANDIDATE_STATUS.PASSED,
    evaluationStatus: CANDIDATE_STATUS.PASSED,
    notes:
      "After assessment evaluation, the candidate-level status becomes Passed or Failed and current round becomes Open Ended.",
  },
  {
    step: "8. AI rejects assessment",
    round: EVALUATION_ROUNDS.OPEN_ENDED,
    status: CANDIDATE_STATUS.FAILED,
    evaluationStatus: CANDIDATE_STATUS.FAILED,
    notes:
      "A failed assessment ends the assessment pipeline unless someone manually reopens or changes the candidate.",
  },
] as const;

const interviewFlow = [
  {
    round: EVALUATION_ROUNDS.CAMPUS_INTERVIEW,
    whenPassed: `Candidate status becomes ${getCandidateStatusLabel(CANDIDATE_STATUS.PENDING)} and current round moves to ${getEvaluationRoundLabel(EVALUATION_ROUNDS.OFFICE_TECHNICAL)}.`,
  },
  {
    round: EVALUATION_ROUNDS.OFFICE_TECHNICAL,
    whenPassed: `Candidate status becomes ${getCandidateStatusLabel(CANDIDATE_STATUS.PENDING)} and current round moves to ${getEvaluationRoundLabel(EVALUATION_ROUNDS.HR_FOUNDER)}.`,
  },
  {
    round: EVALUATION_ROUNDS.HR_FOUNDER,
    whenPassed: `Candidate status becomes ${getCandidateStatusLabel(CANDIDATE_STATUS.OFFERED)}. From here the later hiring statuses such as ${getCandidateStatusLabel(CANDIDATE_STATUS.SELECTED)}, ${getCandidateStatusLabel(CANDIDATE_STATUS.ACCEPTED)}, ${getCandidateStatusLabel(CANDIDATE_STATUS.JOINED)}, or ${getCandidateStatusLabel(CANDIDATE_STATUS.DECLINED)} can be used manually.`,
  },
] as const;

const filterGuidance = [
  {
    name: "Current Round filter",
    description:
      "Shows where the candidate currently is in the pipeline: CV/Profile, MCQ/Open Ended, Voice Interview, 1:1 in Campus, 1:1 in Office Technical, or HR/Founder.",
  },
  {
    name: "Status filter",
    description:
      "Shows the candidate-level stage or outcome. This mixes pipeline states (CV Submitted, Assessment Sent, Assessment In Progress) with round outcomes (Passed, Failed, On Hold) and final hiring outcomes (Offered, Accepted, Joined, etc.).",
  },
  {
    name: "Email Sent filter",
    description:
      "Separates candidates who already received the assessment invite from candidates who have not been invited yet.",
  },
  {
    name: "Assessment round option",
    description:
      "In Current Round, the MCQ/Open Ended option groups both assessment rounds together so admins can quickly isolate the assessment stage.",
  },
] as const;

const terminology = [
  {
    label: "Current Status",
    description:
      "Stored on the candidate record. Use this to understand the candidate's present stage or top-level outcome.",
  },
  {
    label: "Current Round",
    description:
      "Stored on the candidate record. Use this to understand which round the candidate is currently sitting in.",
  },
  {
    label: "Evaluation Status",
    description:
      "Stored per round in evaluation history. Use this to understand whether a specific round was Passed, Failed, or On Hold.",
  },
] as const;

const statusBadgeClassName =
  "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-50";

export default function CandidateJourneyGuidePage() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <p className="text-sm font-medium text-blue-600">Admin Documentation</p>
        <h1 className="text-3xl font-bold tracking-tight">
          Candidate status journey
        </h1>
        <p className="max-w-4xl text-muted-foreground">
          This page explains how candidate status, current round, and round-level
          evaluation status work together so admins can read the pipeline without
          confusion.
        </p>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Most important rule</AlertTitle>
        <AlertDescription>
          Always read <strong>Current Status</strong> together with{" "}
          <strong>Current Round</strong>. A status like Passed can mean “passed the
          current round”, while Pending usually means “the candidate cleared the
          previous round and is now waiting in the next round”.
        </AlertDescription>
      </Alert>

      <div className="grid gap-4 md:grid-cols-3">
        {terminology.map((item) => (
          <Card key={item.label}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{item.label}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{item.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Main lifecycle
          </CardTitle>
          <CardDescription>
            This follows the actual status transitions used by the app for CV,
            assessment, and AI evaluation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Stage</TableHead>
                <TableHead>Current Round</TableHead>
                <TableHead>Current Status</TableHead>
                <TableHead>Evaluation Status</TableHead>
                <TableHead>What it means</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lifecycleSteps.map((step) => (
                <TableRow key={step.step}>
                  <TableCell className="font-medium">{step.step}</TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {getEvaluationRoundLabel(step.round)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={statusBadgeClassName}>
                      {getCandidateStatusLabel(step.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {step.evaluationStatus}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {step.notes}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GitBranch className="h-5 w-5" />
              Interview round progression
            </CardTitle>
            <CardDescription>
              After the assessment is passed, interview rounds use both Current
              Round and Current Status together.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
              Once a candidate passes an interview round, the app usually moves the
              candidate to the next round with status <strong>Pending</strong>. That
              is why Pending often means <strong>ready for the next round</strong>, not
              <strong>still under the old round</strong>.
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Interview round</TableHead>
                  <TableHead>If this round is passed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {interviewFlow.map((item) => (
                  <TableRow key={item.round}>
                    <TableCell>
                      <Badge variant="outline">
                        {getEvaluationRoundLabel(item.round)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {item.whenPassed}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>How to read Passed</CardTitle>
            <CardDescription>
              The same word can mean different things depending on where you look.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <div className="rounded-lg border p-4">
              <p className="font-medium text-foreground">Passed on candidate record</p>
              <p>
                Means the candidate currently has a positive outcome at the top
                level for the active round, such as CV passed or assessment passed.
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="font-medium text-foreground">Passed in evaluation history</p>
              <p>
                Means a specific round result is passed. After campus or office
                interview pass, the candidate often moves forward as Pending in the
                next round.
              </p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="font-medium text-foreground">Pending</p>
              <p>
                Usually means the candidate is now lined up for the next interview
                round and the result for that next round is not decided yet.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filter guide
          </CardTitle>
          <CardDescription>
            These are the main filters admins use to separate candidates.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          {filterGuidance.map((item) => (
            <div key={item.name} className="rounded-lg border p-4">
              <p className="font-medium text-foreground">{item.name}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                {item.description}
              </p>
            </div>
          ))}
        </CardContent>
      </Card>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>One terminology note for admins</AlertTitle>
        <AlertDescription>
          In everyday discussion, people may say <strong>Submitted</strong> after a
          candidate finishes the assessment. In the system, that temporary state is
          stored as <strong>{getCandidateStatusLabel(CANDIDATE_STATUS.ASSESSMENT_COMPLETED)}</strong>
          . After AI evaluation, the candidate usually changes again to{" "}
          <strong>{getCandidateStatusLabel(CANDIDATE_STATUS.PASSED)}</strong> or{" "}
          <strong>{getCandidateStatusLabel(CANDIDATE_STATUS.FAILED)}</strong>.
        </AlertDescription>
      </Alert>
    </div>
  );
}
